import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  activateInvestmentAccount,
  createInvestorAddressDetailsApi,
  createInvestorAccountApi,
  createInvestorBankDetailsApi,
  createInvestorContactInfoApi,
  createInvestorNomineeDetailsApi,
  createInvestorProfileApi,
  createOrLinkHolderProfileApi,
  approveHolderProfileApi,
  sendHolderAuthenticationOtpApi,
  getInvestorAddressDetailsApi,
  getInvestorBankDetailsApi,
  getInvestorContactInfoApi,
  getInvestorInvestmentAccountApi,
  getInvestorNomineeDetailsApi,
  getInvestorProfileApi,
  updateInvestmentAccount,
  updateInvestorAddressDetailsApi,
  updateInvestorBankDetailsApi,
  updateInvestorContactInfoApi,
  updateInvestorNomineeDetailsApi,
  updateInvestorProfileApi,
  notifyInvestorApi,
  uploadElogApi,
  verifyBankAccount,
  checkKYCStatusApi,
  registerKYCApi,
  verifyProposedBankDetails,
  getSavedUPIAddressesApi,
  createInvestmentAccount,
  getRiskProfileQuestions,
  submitRiskProfile,
  sendRiskProfileQuestionnare,
} from "../../api/clients/account";
import { useClient } from "./client";
import { GetInvestorProfilesParams, InvestorNomineeDetails } from "../../types";

export const investorContactInfoQueryOptions = (
  investmentAccountGuid?: string
) =>
  queryOptions({
    queryKey: [
      "investor/contact-info",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getInvestorContactInfoApi(investmentAccountGuid),
    select: (data) => data?.[0],
    enabled: !!investmentAccountGuid,
  });

export const useInvestorContactInfo = (investmentAccountGuid?: string) => {
  return useQuery(investorContactInfoQueryOptions(investmentAccountGuid));
};

export const useCreateInvestorContactInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["investor/contact-info/create"],
    mutationFn: createInvestorContactInfoApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/contact-info"] });
    },
  });
};

export const useUpdateInvestorContactInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInvestorContactInfoApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/contact-info"] });
    },
  });
};
export const investorProfileQueryOptions = (
  params: GetInvestorProfilesParams | string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const resolved =
    typeof params === "string" ? { investment_account_guid: params } : params;

  return queryOptions({
    queryKey: ["investor/profile", resolved],
    queryFn: () => getInvestorProfileApi(resolved),
    enabled:
      enabled && (!!resolved.client_id || !!resolved.investment_account_guid),
  });
};

export const useInvestorProfile = (
  params: GetInvestorProfilesParams | string,
  options?: { enabled?: boolean }
) =>
  useQuery({
    ...investorProfileQueryOptions(params, options),
    select: (data) => data[0],
  });

export const useCreateInvestorProfileDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createInvestorProfileApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/profile"] });
    },
  });
};

export const useCreateOrLinkHolderProfile = () => {
  return useMutation({
    mutationFn: createOrLinkHolderProfileApi,
  });
};

export const useApproveHolderProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveHolderProfileApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/profile"] });
    },
  });
};

export const useSendHolderAuthenticationOtp = () =>
  useMutation({ mutationFn: sendHolderAuthenticationOtpApi });

export const useUpdateInvestorProfileDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInvestorProfileApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/profile"] });
    },
  });
};

export const investorPrimaryBankDetailsQueryOptions = (
  investmentAccountGuid: string,
  all: boolean = false,
  enabled = true
) =>
  queryOptions({
    queryKey: [
      "investor/primary-bank-account",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getInvestorBankDetailsApi(investmentAccountGuid),
    enabled: !!investmentAccountGuid && enabled,
    select: all
      ? undefined
      : (data) => data.find((d) => d.is_default) || data?.[0],
  });

export const useInvestorPrimaryBankAccount = (
  investmentAccountGuid: string,
  enabled?: boolean
) => {
  return useQuery(
    investorPrimaryBankDetailsQueryOptions(
      investmentAccountGuid,
      false,
      enabled
    )
  );
};

const investorBankAccountsQueryOptions = (investmentAccountGuid: string) =>
  queryOptions({
    queryKey: [
      "investor/bank-accounts",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getInvestorBankDetailsApi(investmentAccountGuid),
    enabled: !!investmentAccountGuid,
  });

export const useInvestorBankAccounts = (investmentAccountGuid: string) => {
  return useQuery(investorBankAccountsQueryOptions(investmentAccountGuid));
};

export const useCreateInvestorBankDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInvestorBankDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investor/primary-bank-account"],
      });
      queryClient.invalidateQueries({
        queryKey: ["investor/bank-accounts"],
      });
    },
  });
};

export const useUpdateInvestorBankDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateInvestorBankDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investor/primary-bank-account"],
      });
      queryClient.invalidateQueries({
        queryKey: ["investor/bank-accounts"],
      });
    },
  });
};

export const investorAddressDetailsQueryOptions = (
  investmentAccountGuid: string
) =>
  queryOptions({
    queryKey: [
      "investor/address-details",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getInvestorAddressDetailsApi(investmentAccountGuid),
    enabled: !!investmentAccountGuid,
    select: (data) => data?.[0],
  });

export const useInvestorAddressDetails = (investmentAccountGuid: string) => {
  return useQuery(investorAddressDetailsQueryOptions(investmentAccountGuid));
};

export const useUpdateInvestorAddressDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateInvestorAddressDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/address-details"] });
    },
  });
};

export const useCreateInvestorAddressDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInvestorAddressDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/address-details"] });
    },
  });
};

export const getInvestorInvestmentAccountOptions = (
  userGuid?: string,
  investmentAccountGuid?: string,
  enabled: boolean = true
) =>
  queryOptions({
    queryKey: [
      "investor/investment-account",
      { user_guid: userGuid, investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () =>
      getInvestorInvestmentAccountApi(userGuid, investmentAccountGuid),
    enabled: enabled && (!!userGuid || !!investmentAccountGuid),
  });

export const useInvestorInvestmentAccount = (
  userGuid?: string,
  investmentAccountGuid?: string,
  enabled?: boolean
) => {
  return useQuery(
    getInvestorInvestmentAccountOptions(
      userGuid,
      investmentAccountGuid,
      enabled
    )
  );
};

export const nomineeDetailsOptions = (investmentAccountId: string) =>
  queryOptions({
    queryKey: ["investor/nominee-details", { investmentAccountId }],
    queryFn: () => getInvestorNomineeDetailsApi(investmentAccountId),
    retry: 0,
    select: (data) => data?.[0],
    enabled: !!investmentAccountId,
  });

export const useNomineeDetails = (investmentAccountId: string) => {
  return useQuery(nomineeDetailsOptions(investmentAccountId));
};

export const investorNomineesQueryOption = (investmentAccountId: string) =>
  queryOptions({
    queryKey: ["investor/nominees", { investmentAccountId }],
    queryFn: () => getInvestorNomineeDetailsApi(investmentAccountId),
    enabled: !!investmentAccountId,
  });

export const useNominees = (investmentAccountId: string) =>
  useQuery(investorNomineesQueryOption(investmentAccountId));

export const useCreateNomineeDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createInvestorNomineeDetailsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/nominee-details"] });
      queryClient.invalidateQueries({ queryKey: ["investor/nominees"] });
    },
  });
};

export const useUpdateNomineeDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Omit<InvestorNomineeDetails, "investment_account">) =>
      updateInvestorNomineeDetailsApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investor/nominee-details"] });
      queryClient.invalidateQueries({ queryKey: ["investor/nominees"] });
    },
  });
};

export const useActivateInvestmentAccount = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => activateInvestmentAccount({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investor/investment-account"],
      });
    },
  });
};

export const useUpdateInvestorInvestmentAccount = (
  investmentAccountGuid: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [
      "investor/investment-account/update",
      { investment_account_guid: investmentAccountGuid },
    ],
    mutationFn: ({
      nominationOpt,
      nomineeSoaOpt,
    }: {
      nominationOpt: "N" | "Y";
      nomineeSoaOpt?: "N" | "Y";
    }) =>
      updateInvestmentAccount({
        id: investmentAccountGuid,
        data: {
          nomination_opt: nominationOpt,
          nominee_soa_opt: nomineeSoaOpt,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investor/investment-account"],
      });
    },
  });
};

export const useInvestorInfo = (clientId: number) => {
  const { data: client } = useClient(clientId);
  const investmentAccountId = client?.investment_account ?? "";

  return useQueries({
    queries: [
      investorAddressDetailsQueryOptions(investmentAccountId),
      investorBankAccountsQueryOptions(investmentAccountId),
      investorNomineesQueryOption(investmentAccountId),
      investorContactInfoQueryOptions(investmentAccountId),
      investorProfileQueryOptions(investmentAccountId),
      getInvestorInvestmentAccountOptions("", investmentAccountId),
    ],
    combine(result) {
      return {
        isLoading: result.some((r) => r.isLoading),
        isError: result.some((r) => r.isError),
        data: {
          client,
          address: result[0].data,
          bank: result[1].data,
          nominee: result[2].data,
          contact: result[3].data,
          profile: result[4].data?.[0],
          account: result[5].data?.[0],
        },
      };
    },
  });
};

export const useCreateInvestorAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pan, arnCode }: { pan: string; arnCode: string }) =>
      createInvestorAccountApi({ pan, arn_code: arnCode }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investor/investment-account"],
      });
    },
  });
};

export const useNotifyInvestorMutation = () => {
  return useMutation({
    mutationFn: (investmentAccountGuid: string) =>
      notifyInvestorApi(investmentAccountGuid),
  });
};

export const useElogUploadMutation = () => {
  return useMutation({
    mutationKey: ["investor/elog-upload"],
    mutationFn: uploadElogApi,
  });
};

export const useVerifyBankDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["investor/bank-account-verification"],
    mutationFn: verifyBankAccount,
    // Verification updates the bank's status, which the bank list shows too
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["investor/primary-bank-account"],
      });
      queryClient.invalidateQueries({
        queryKey: ["investor/bank-accounts"],
      });
    },
  });
};

export const useVerifyProposedBankDetails = () => {
  return useMutation({
    mutationKey: ["verify-proposed-bank-details"],
    mutationFn: verifyProposedBankDetails,
  });
};

export const useKYCStatus = (clientId: number | string, enabled?: boolean) => {
  return useQuery({
    queryKey: ["investor/kyc-status", { client_id: clientId }],
    queryFn: () => checkKYCStatusApi(clientId),
    enabled,
  });
};

export const useFetchKycStatus = () => {
  return useMutation({
    mutationFn: (
      payload: number | { client_id?: number | string; pan?: string }
    ) => checkKYCStatusApi(payload),
  });
};

export const useRegisterKYC = (clientId: number | string) => {
  return useMutation({
    mutationFn: () => registerKYCApi(clientId),
  });
};

export const useSavedUPIAddresses = (investmentAccountGuid: string) => {
  return useQuery({
    queryKey: [
      "investor/saved-upi-addresses",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getSavedUPIAddressesApi(investmentAccountGuid),
  });
};

export const useCreateInvestmentAccount = () => {
  return useMutation({
    mutationFn: createInvestmentAccount,
  });
};

export const riskProfileQuestionsQueryOptions = (enabled: boolean = true) =>
  queryOptions({
    queryKey: ["risk-profile/questions"],
    queryFn: () => getRiskProfileQuestions(),
    enabled,
  });

export const useRiskProfileQuestions = (enabled?: boolean) => {
  return useQuery(riskProfileQuestionsQueryOptions(enabled));
};

export const useSubmitRiskProfile = () => {
  return useMutation({
    mutationKey: ["risk-profile/submit"],
    mutationFn: submitRiskProfile,
  });
};

export function useSendRiskProfileQuestionnare() {
  return useMutation({
    mutationKey: ["risk-profile/send"],
    mutationFn: sendRiskProfileQuestionnare,
  });
}
